import SaltLogo from "../V2/Icons/SaltLogo";
import Button from "../V2/Button/Button";
import { useEffect, useRef, useState } from "react";
import ProgressBar from "./ProgressBar";
import ChevronLeft from "../Icons/ChevronLeft";
import ChevronRight from "../Icons/ChevronRight";
import Question from "./Question";
import { QuestionType } from "@/lib/utils/mpt";
import { AnimatePresence, motion } from "framer-motion";
import { AppEvent, trackEvent } from "@/lib/utils/events";
import BlurOverlay from "../BlurOverlay/BlurOverlay";
import Spinner from "../Spinner";

const Questions = ({
	questions,
	isSubmitting,
	onAnswer,
}: {
	questions: Array<QuestionType>;
	isSubmitting: boolean;
	onAnswer: (id: number, answer: number) => void;
}) => {
	const [currentIndex, setCurrentIndex] = useState(0);
	const actualQuestions = useRef(questions.filter((f) => f.type !== 0));

	useEffect(() => {
		const eventLabel = questions[currentIndex]?.event_name as
			| "mpt_life_questions"
			| "mpt_money_questions";
		if (eventLabel) {
			trackEvent(AppEvent.screenView, {
				product: "mpt",
				label: eventLabel,
				screen: "mpt_questions",
			});
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentIndex]);

	const handleNav = (dir: "prev" | "next") => {
		if (dir === "next" && currentIndex < questions.length - 1) {
			setCurrentIndex((i) => i + 1);
		} else if (dir === "prev" && currentIndex > 0) {
			if (questions[currentIndex - 1]?.type === 0 && currentIndex > 1) {
				setCurrentIndex((i) => i - 2);
			} else {
				setCurrentIndex((i) => i - 1);
			}
		}
	};

	const handleAnswerUpdate = (id: number, answer: number) => {
		onAnswer(id, answer);
		handleNav("next");
	};

	const currentQuestion = questions[currentIndex];

	const isNextButtonDisabled =
		currentQuestion?.type !== 0 &&
		(currentQuestion?.answer === "" || currentQuestion?.answer === null);

	return (
		<>
			<div className="pb-10 flex flex-direction-column" style={{ flex: 1 }}>
				{isSubmitting && (
					<>
						<BlurOverlay />
						<div
							className="position-absolute flex justify-content-center align-items-center mt-12"
							style={{
								width: `100%`,
								maxWidth: `600px`,
								zIndex: 10,
							}}
						>
							<Spinner color="var(--primary-500)" />
						</div>
					</>
				)}
				<div>
					<div className="flex justify-content-center">
						<SaltLogo
							color="var(--primary-500)"
							height={currentQuestion?.type === 0 ? 50 : 32}
							width={currentQuestion?.type === 0 ? 100 : 64}
						/>
					</div>
					{currentQuestion?.type !== 0 && (
						<div className="fs-12 fw-400 color-abyssal-blue-300 text-center mt-x-3 font-secondary">
							Know your money to grow your money
						</div>
					)}
				</div>

				<AnimatePresence>
					{currentQuestion?.type === 0 ? (
						<>
							{currentQuestion?.image?.url && (
								<img
									src={currentQuestion.image.url}
									className="mlr-auto mt-4"
									alt="image"
									width={`280px`}
									height={`280px`}
								/>
							)}
							{currentQuestion?.text && (
								<div
									className="mtb-3 plr-5 fs-20 fw-500 font-secondary mlr-auto"
									dangerouslySetInnerHTML={{ __html: currentQuestion.text }}
								/>
							)}
							<div className="flex justify-content-center ptb-2 plr-3 mt-auto">
								<Button
									variant="dark"
									style={{
										height: `40px`,
										padding: `0 var(--sp-03)`,
									}}
									onClick={() => handleNav("next")}
								>
									Next&nbsp;&nbsp;
									<ChevronRight color="#FFF" height={14} />
								</Button>
							</div>
						</>
					) : (
						<>
							<div
								className="position-relative"
								style={{
									overflowX: `hidden`,
									flex: 1,
									width: `100%`,
								}}
							>
								{questions.map((qs, i) => (
									<motion.div
										key={qs.id}
										style={{
											position: `absolute`,
											width: `80%`,
											height: `100%`,
											marginLeft: `var(--sp-04)`,
											marginRight: `var(--sp-02)`,
											zIndex: currentIndex === i ? 7 : 6,
										}}
										initial={{
											x:
												currentIndex === i
													? 0
													: i == currentIndex + 1
													? `105%`
													: i > currentIndex + 1
													? `${(i - currentIndex) * 100}%`
													: i < currentIndex
													? `-115%`
													: 0,
											scaleY: i !== currentIndex ? 0.9 : 1,
										}}
										animate={{
											x:
												currentIndex === i
													? 0
													: i == currentIndex + 1
													? `105%`
													: i > currentIndex + 1
													? `${(i - currentIndex) * 100}%`
													: i < currentIndex
													? `-115%`
													: 0,
											scaleY: i !== currentIndex ? 0.9 : 1,
										}}
										className={`${
											currentIndex === i
												? `a`
												: i === currentIndex + 1
												? `n`
												: i === currentIndex - 1
												? `p`
												: `vis-0`
										}`}
										layout
										transition={{
											type: "spring",
											stiffness: 70,
											delay: 0.3,
										}}
									>
										<Question
											question={qs}
											color={qs.color!}
											questionIndex={qs.idx!}
											onSelect={(answer) => handleAnswerUpdate(qs.id, answer)}
										/>
									</motion.div>
								))}
							</div>

							<div className="flex gap-5 justify-content-center">
								{questions[currentIndex].id !==
									actualQuestions.current[0].id && (
									<span className="nav" onClick={() => handleNav("prev")}>
										<ChevronLeft size={18} color="var(--abyssal-blue-300)" />
									</span>
								)}
								{currentIndex < questions.length - 1 && (
									<span
										className={`nav ${isNextButtonDisabled ? `disabled` : ``}`}
										onClick={() => !isNextButtonDisabled && handleNav("next")}
									>
										<ChevronRight
											height={12}
											width={12}
											color={`${
												isNextButtonDisabled
													? `var(--abyssal-blue-100)`
													: `var(--abyssal-blue-300)`
											}`}
										/>
									</span>
								)}
							</div>
						</>
					)}
					{currentQuestion && currentQuestion?.type !== 0 && (
						<motion.div
							key={`progress`}
							style={{
								position: `fixed`,
								width: `100%`,
								maxWidth: `600px`,
							}}
							initial={{
								bottom: `-100px`,
							}}
							animate={{
								opacity: 1,
								bottom: 0,
								transition: {
									delay: 0.3,
								},
							}}
							exit={{
								bottom: `-100px`,
							}}
						>
							<ProgressBar
								percentage={currentQuestion?.completionPercentage || 0}
								remainingTime={currentQuestion?.remainingTimeText!}
							/>
						</motion.div>
					)}
				</AnimatePresence>
			</div>
			<style jsx>{`
				.nav {
					height: 32px;
					width: 32px;
					border: 1px solid var(--primary-300);
					border-radius: 50%;
					display: flex;
					align-items: center;
					justify-content: center;
					cursor: pointer;
				}
				.disabled {
					border: 1px solid var(--primary-100);
					pointer-events: none;
				}
			`}</style>
		</>
	);
};

export default Questions;
